const MOODLE_LOGOUT_PATH = '/auth/oidc/logout.php';
const MOODLE_OIDC_PATH = '/auth/oidc/';

const MOODLE_ORIGIN_BY_PLATFORM_HOST = {
  'platform-staging.deviare.africa': 'https://lms-staging.deviare.africa',
  'platform.deviare.africa': 'https://lms.deviare.africa',
};

const MOODLE_LMS_HOSTS = new Set([
  'lms-staging.deviare.africa',
  'lms.deviare.africa',
]);

const isMoodleLmsHostname = (hostname) => {
  if (!hostname) {
    return false;
  }
  if (MOODLE_LMS_HOSTS.has(hostname)) {
    return true;
  }
  return hostname.startsWith('lms') && hostname.endsWith('.deviare.africa');
};

export const getMoodleOidcLogoutUrl = () => {
  if (typeof window === 'undefined') {
    return null;
  }

  const { hostname, origin } = window.location;
  const moodleOrigin = MOODLE_ORIGIN_BY_PLATFORM_HOST[hostname];
  if (!moodleOrigin) {
    return null;
  }

  return `${moodleOrigin}${MOODLE_LOGOUT_PATH}?redirect=${encodeURIComponent(origin)}`;
};

export const goToMoodleLogoutOrKeycloak = (keycloak) => {
  const moodleLogoutUrl = getMoodleOidcLogoutUrl();
  let finished = false;

  const finishKeycloakLogout = () => {
    if (finished) {
      return;
    }
    finished = true;
    if (keycloak && typeof keycloak.logout === 'function') {
      keycloak.logout({ redirectUri: `${window.location.origin}/` });
    }
  };

  if (!moodleLogoutUrl) {
    finishKeycloakLogout();
    return;
  }

  // Moodle logout.php currently returns a blank 200 instead of 303 to Keycloak.
  // Do not navigate the tab there or the user is stranded on LMS and Keycloak
  // still signs them back into the platform. Hit Moodle in a hidden iframe so
  // MoodleSession is sent (same-site), then always end the Keycloak session.
  const iframe = document.createElement('iframe');
  iframe.setAttribute('aria-hidden', 'true');
  iframe.title = '';
  iframe.style.cssText = 'position:absolute;width:0;height:0;border:0;visibility:hidden';
  iframe.src = moodleLogoutUrl;
  iframe.onload = finishKeycloakLogout;
  iframe.onerror = finishKeycloakLogout;
  document.body.appendChild(iframe);
  window.setTimeout(finishKeycloakLogout, 2500);
};


export const withMoodleIdentityCheck = (courseUrl) => {
  if (!courseUrl || typeof courseUrl !== 'string') {
    return courseUrl;
  }

  let parsed;
  try {
    parsed = new URL(courseUrl);
  } catch (error) {
    return courseUrl;
  }

  if (!isMoodleLmsHostname(parsed.hostname)) {
    return courseUrl;
  }

  const pathname = parsed.pathname || '/';
  if (pathname.includes('/auth/oidc/logout.php')) {
    return courseUrl;
  }

  const wantsurl = `${parsed.pathname}${parsed.search}${parsed.hash}` || '/';

  if (pathname.includes('/auth/oidc')) {
    parsed.searchParams.set('identitycheck', '1');
    if (!parsed.searchParams.get('wantsurl')) {
      parsed.searchParams.set('wantsurl', '/');
    }
    return parsed.toString();
  }

  const launch = new URL(`${parsed.origin}${MOODLE_OIDC_PATH}`);
  launch.searchParams.set('identitycheck', '1');
  launch.searchParams.set('wantsurl', wantsurl);
  return launch.toString();
};

export const COURSE_IFRAME_CLOSE_EVENT = 'deviare-course-iframe-close';

export const closeCourseIframe = () => {
  if (typeof window === 'undefined') {
    return;
  }
  window.dispatchEvent(new CustomEvent(COURSE_IFRAME_CLOSE_EVENT));
};

export const isMoodleSessionTimeoutMessage = (event) => {
  if (!event?.origin) {
    return false;
  }

  try {
    const { hostname } = new URL(event.origin);
    if (!isMoodleLmsHostname(hostname)) {
      return false;
    }
  } catch (error) {
    return false;
  }

  const data = event.data;
  return Boolean(
    data &&
    data.source === 'deviare-moodle' &&
    data.type === 'moodle-session-timeout'
  );
};

const getLoginRedirectUri = () =>
  `${window.location.origin}${window.location.pathname}${window.location.search}`;

let keycloakLoginStarted = false;

export const goToDeviareLogin = (keycloak) => {
  if (keycloakLoginStarted) {
    return;
  }
  keycloakLoginStarted = true;
  closeCourseIframe();
  if (keycloak && typeof keycloak.login === 'function') {
    keycloak.login({ redirectUri: getLoginRedirectUri() });
  }
};

export const ensureKeycloakSession = (keycloak, minValidity = 60) => {
  if (!keycloak || typeof keycloak.updateToken !== 'function') {
    return Promise.resolve(true);
  }

  if (keycloak.authenticated === false) {
    goToDeviareLogin(keycloak);
    return Promise.resolve(false);
  }

  return keycloak.updateToken(minValidity).then(() => true).catch(() => {
    goToDeviareLogin(keycloak);
    return false;
  });
};

export const watchKeycloakSession = (keycloak) => {
  if (typeof window === 'undefined' || !keycloak) {
    return () => {};
  }

  let checking = false;
  let debounceTimer = 0;

  const refreshOrLogin = () => {
    if (checking || keycloakLoginStarted) {
      return;
    }
    checking = true;
    ensureKeycloakSession(keycloak).finally(() => {
      checking = false;
    });
  };

  const onTabActive = () => {
    if (document.visibilityState === 'hidden') {
      return;
    }
    window.clearTimeout(debounceTimer);
    debounceTimer = window.setTimeout(refreshOrLogin, 300);
  };

  keycloak.onTokenExpired = refreshOrLogin;
  document.addEventListener('visibilitychange', onTabActive);
  window.addEventListener('focus', onTabActive);
  document.addEventListener('resume', onTabActive);

  return () => {
    window.clearTimeout(debounceTimer);
    if (keycloak.onTokenExpired === refreshOrLogin) {
      keycloak.onTokenExpired = undefined;
    }
    document.removeEventListener('visibilitychange', onTabActive);
    window.removeEventListener('focus', onTabActive);
    document.removeEventListener('resume', onTabActive);
  };
};
